import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import { motion } from 'framer-motion';
import { 
  Bell, 
  Mail, 
  Smartphone, 
  Globe, 
  Moon, 
  Palette,
  Volume2,
  Save,
  BellRing,
  Brush,
  Music
} from 'lucide-react';

export function SettingsPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isUpdating, setIsUpdating] = useState(false);

  const handleSave = async () => {
    setIsUpdating(true);
    try {
      // Here you would make an API call to save settings
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      toast({
        title: 'Success',
        description: 'Settings updated successfully',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update settings',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <h1 className="text-3xl font-bold tracking-tight text-primary-700 dark:text-primary-300">
          Settings
        </h1>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-sm px-4 py-2 rounded-full glass-effect text-muted-foreground"
        >
          {user?.email}
        </motion.div>
      </motion.div>

      <Tabs defaultValue="notifications" className="space-y-8">
        <TabsList className="bg-background/50 backdrop-blur-sm">
          <TabsTrigger value="notifications" className="data-[state=active]:bg-primary/10">
            <BellRing className="h-4 w-4 mr-2" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="appearance" className="data-[state=active]:bg-primary/10">
            <Brush className="h-4 w-4 mr-2" />
            Appearance
          </TabsTrigger>
          <TabsTrigger value="sound" className="data-[state=active]:bg-primary/10">
            <Music className="h-4 w-4 mr-2" />
            Sound
          </TabsTrigger>
        </TabsList>

        <TabsContent value="notifications" className="space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card variant="glass" className="overflow-hidden">
              <CardHeader>
                <CardTitle>Notification Preferences</CardTitle>
                <CardDescription>Control how you receive notifications</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-background/30 backdrop-blur-sm transition-all hover:bg-background/40">
                    <div className="flex items-center gap-3">
                      <div className="rounded-full bg-primary/10 p-2 flex items-center justify-center">
                        <Bell className="h-4 w-4 text-primary" />
                      </div>
                      <Label htmlFor="push-notifications" className="font-medium">Push Notifications</Label>
                    </div>
                    <Switch id="push-notifications" defaultChecked />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-background/30 backdrop-blur-sm transition-all hover:bg-background/40">
                    <div className="flex items-center gap-3">
                      <div className="rounded-full bg-primary/10 p-2 flex items-center justify-center">
                        <Mail className="h-4 w-4 text-primary" />
                      </div>
                      <Label htmlFor="email-notifications" className="font-medium">Email Notifications</Label>
                    </div>
                    <Switch id="email-notifications" defaultChecked />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-background/30 backdrop-blur-sm transition-all hover:bg-background/40">
                    <div className="flex items-center gap-3">
                      <div className="rounded-full bg-primary/10 p-2 flex items-center justify-center">
                        <Smartphone className="h-4 w-4 text-primary" />
                      </div>
                      <Label htmlFor="sms-notifications" className="font-medium">SMS Notifications</Label>
                    </div>
                    <Switch id="sms-notifications" />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-background/30 backdrop-blur-sm transition-all hover:bg-background/40">
                    <div className="flex items-center gap-3">
                      <div className="rounded-full bg-primary/10 p-2 flex items-center justify-center">
                        <Globe className="h-4 w-4 text-primary" />
                      </div>
                      <Label htmlFor="browser-notifications" className="font-medium">Browser Notifications</Label>
                    </div>
                    <Switch id="browser-notifications" defaultChecked />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card variant="glass" className="overflow-hidden">
              <CardHeader>
                <CardTitle>Notification Types</CardTitle>
                <CardDescription>Choose what types of notifications you want to receive</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-background/30 backdrop-blur-sm transition-all hover:bg-background/40">
                    <Label htmlFor="task-updates" className="font-medium">Task Updates</Label>
                    <Switch id="task-updates" defaultChecked />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-background/30 backdrop-blur-sm transition-all hover:bg-background/40">
                    <Label htmlFor="maintenance-alerts" className="font-medium">Maintenance Alerts</Label>
                    <Switch id="maintenance-alerts" defaultChecked />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-background/30 backdrop-blur-sm transition-all hover:bg-background/40">
                    <Label htmlFor="billing-notifications" className="font-medium">Billing Notifications</Label>
                    <Switch id="billing-notifications" defaultChecked />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-background/30 backdrop-blur-sm transition-all hover:bg-background/40">
                    <Label htmlFor="system-updates" className="font-medium">System Updates</Label>
                    <Switch id="system-updates" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        <TabsContent value="appearance" className="space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card variant="glass" className="overflow-hidden">
              <CardHeader>
                <CardTitle>Theme Settings</CardTitle>
                <CardDescription>Customize the appearance of the application</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-background/30 backdrop-blur-sm transition-all hover:bg-background/40">
                    <div className="flex items-center gap-3">
                      <div className="rounded-full bg-primary/10 p-2 flex items-center justify-center">
                        <Moon className="h-4 w-4 text-primary" />
                      </div>
                      <Label htmlFor="dark-mode" className="font-medium">Dark Mode</Label>
                    </div>
                    <Switch id="dark-mode" />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-background/30 backdrop-blur-sm transition-all hover:bg-background/40">
                    <div className="flex items-center gap-3">
                      <div className="rounded-full bg-primary/10 p-2 flex items-center justify-center">
                        <Palette className="h-4 w-4 text-primary" />
                      </div>
                      <Label htmlFor="high-contrast" className="font-medium">High Contrast</Label>
                    </div>
                    <Switch id="high-contrast" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        <TabsContent value="sound" className="space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card variant="glass" className="overflow-hidden">
              <CardHeader>
                <CardTitle>Sound Settings</CardTitle>
                <CardDescription>Configure audio notification settings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-background/30 backdrop-blur-sm transition-all hover:bg-background/40">
                    <div className="flex items-center gap-3">
                      <div className="rounded-full bg-primary/10 p-2 flex items-center justify-center">
                        <Volume2 className="h-4 w-4 text-primary" />
                      </div>
                      <Label htmlFor="notification-sounds" className="font-medium">Notification Sounds</Label>
                    </div>
                    <Switch id="notification-sounds" defaultChecked />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>
      </Tabs>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="flex justify-end"
      >
        <Button onClick={handleSave} disabled={isUpdating} className="gap-2">
          <Save className="h-4 w-4" />
          {isUpdating ? 'Saving...' : 'Save Changes'}
        </Button>
      </motion.div>
    </div>
  );
}